import {
  getMediaContentVersion,
  getMediaFileUrl,
  getMediaThumbUrl,
} from '../../../api/mediaUrls';
import { MediaItem } from '../../../api/structs/media';

export interface SceneGroup {
  sceneId: string;
  image?: MediaItem;
  video?: MediaItem;
  allItems: MediaItem[];
  order: number;
}

export const getSceneId = (item: MediaItem): string =>
  String(item.metadata?.scene_id || item.metadata?.sceneId || '');

export const getOrder = (item: MediaItem): number =>
  Number(item.metadata?.order ?? 9999);

// Audios are excluded from the storyboard - they live in MusicTrack only.
export const groupMediaByScene = (
  images: MediaItem[],
  aiVideos: MediaItem[]
): SceneGroup[] => {
  const map = new Map<string, SceneGroup>();
  let ungroupedIdx = 0;

  const addItem = (item: MediaItem, role: 'image' | 'video') => {
    const sceneId = getSceneId(item) || `ungrouped-${ungroupedIdx++}`;
    if (!map.has(sceneId)) {
      map.set(sceneId, { sceneId, allItems: [], order: getOrder(item) });
    }
    const group = map.get(sceneId)!;
    group.allItems.push(item);
    group.order = Math.min(group.order, getOrder(item));
    if (role === 'image' && !group.image) group.image = item;
    if (role === 'video' && !group.video) group.video = item;
  };

  images.forEach((item) => addItem(item, 'image'));
  aiVideos.forEach((item) => addItem(item, 'video'));

  return Array.from(map.values()).sort((a, b) => a.order - b.order);
};

/**
 * Content-version cache-busting lives in `src/api/mediaUrls.ts` — the clip
 * editor needs the same URLs. Re-exported so existing callers of this module
 * (and its tests) keep their import path.
 */
export { getMediaContentVersion, getMediaFileUrl, getMediaThumbUrl };

export interface SceneThumb {
  /** Preferred small webp derivative (may 404 → fall back to `originalUrl`). */
  thumbUrl: string;
  /** Full-resolution still-image original — the fallback for the derivative. */
  originalUrl: string;
  /** Playable video URL, set only for video-only scenes (no still image). The
   *  original there is an mp4 and must never be fed to an <img>. */
  videoUrl: string;
  /** True while the primary asset is still being generated (spinner, not an
   *  <img>). NOT true for a failed one — see `failed`. */
  waiting: boolean;
  /** True when the primary asset's own generation failed and it is parked on
   *  the `_failed` placeholder. It will never finish, so the spinner is a lie:
   *  render a failed state instead. */
  failed: boolean;
}

/**
 * Resolve the storyboard thumbnail candidates for a scene. Prefers the still
 * image (its cheap webp derivative + original) and only falls back to a
 * video-first-frame for video-only scenes.
 */
export const getSceneThumb = (scene: SceneGroup): SceneThumb => {
  const image = scene.image;
  if (image?.file_url) {
    return {
      thumbUrl: getMediaThumbUrl(image),
      originalUrl: getMediaFileUrl(image),
      videoUrl: '',
      waiting: isGenerating(image.file_url),
      failed: isFailed(image.file_url),
    };
  }
  const video = scene.video;
  if (video?.file_url) {
    return {
      thumbUrl: getMediaThumbUrl(video),
      originalUrl: '',
      videoUrl: getMediaFileUrl(video),
      waiting: isGenerating(video.file_url),
      failed: isFailed(video.file_url),
    };
  }
  return { thumbUrl: '', originalUrl: '', videoUrl: '', waiting: false, failed: false };
};

/**
 * The item is still being generated — bytes are coming. This is the ONLY state
 * a spinner is honest about.
 */
export const isGenerating = (url: string) => url.includes('_waiting');

/**
 * The item's own generation failed and it is parked on `_failed.png`. Terminal:
 * nothing further will arrive without another regenerate.
 *
 * Split out of `isWaiting`, which conflated the two — so a genuinely failed
 * item spun a "generating…" spinner forever, and the one state the user needed
 * to see was rendered as the state that says "wait a bit longer".
 */
export const isFailed = (url: string) => url.includes('_failed');

/**
 * Neither placeholder holds real bytes. This is what `isWaiting` has always
 * MEANT at every call site that asks "is there something playable here?" — the
 * name is kept because `clip-editor/useSceneAlternates` imports it to filter
 * unplayable takes out of the strip, where both placeholders must be excluded
 * and the distinction genuinely does not matter.
 *
 * Prefer `isGenerating` / `isFailed` in new code: this one cannot answer the
 * question the storyboard actually asks.
 */
export const isPlaceholder = (url: string) => isGenerating(url) || isFailed(url);

/** @deprecated Use `isPlaceholder` (same behaviour) or, better, the specific test. */
export const isWaiting = isPlaceholder;

export const getLipSyncStatus = (item: MediaItem): string | null => {
  if (item.metadata?.needs_lip_sync || item.metadata?.lip_sync_pending) return 'pending';
  if (item.metadata?.lip_sync_source_id) return 'done';
  return null;
};
